/**
 * SC-255 — the in-game chrome the zoom indicator measures.
 *
 * These are **containers, not buttons**, and that is the whole point. A registry of individual
 * CTAs rots silently: someone adds a button, forgets to register it, nothing fails, and the
 * indicator quietly stops covering part of the UI. `bottom-chrome` is the lane that every in-room
 * bottom control already lives in, so new toolbar items (declared in `GameMenu/features/registry.ts`
 * and rendered inside it) are measured with no code change at all.
 *
 * A module-level registry rather than a DOM sweep because the anchors mount asynchronously (the
 * lazy `GameMenuController` chunk) and one of them renders through a portal into `document.body`.
 * React refs handle both without observing a subtree that Phaser mutates every frame.
 */

export type AnchorId = 'bottom-chrome' | 'chat-launcher'

const anchors = new Map<AnchorId, HTMLElement>()
const listeners = new Set<() => void>()

function notify(): void {
  listeners.forEach((fn) => fn())
}

/** Pass `null` to unregister — this is called directly as a React ref callback. */
export function setAnchor(id: AnchorId, el: HTMLElement | null): void {
  if (el) {
    if (anchors.get(id) === el) return
    anchors.set(id, el)
  } else {
    if (!anchors.has(id)) return
    anchors.delete(id)
  }
  notify()
}

export function getAnchors(): ReadonlyArray<[AnchorId, HTMLElement]> {
  return Array.from(anchors.entries())
}

export function subscribeAnchors(fn: () => void): () => void {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}

/** Test-only: the registry is module state and would otherwise leak between cases. */
export function resetAnchorsForTest(): void {
  anchors.clear()
  listeners.clear()
}
